import {
  CARRY_ACCEL_MIN_MULT,
  CARRY_LIGHT_KG,
  CARRY_MAX_KG,
  CARRY_SPEED_MIN_MULT,
} from "../constants.js";
import type { MoveMods } from "./movement.js";

/**
 * Kilograms carried, turned into how you move — the one place the ramp is
 * expressed, called by both sides with the same number (see the Encumbrance
 * block in `constants.ts`). A plain number, not an `Inventory`, so the module
 * is free of the item layer. `carryMult` (strength) DIVIDES the weight rather
 * than scaling the thresholds: one operation on one number, so the authored
 * rails stay exact and `moveModsFromWeight(CARRY_LIGHT_KG)` is still `{ 1, 1 }`.
 * The default 1 is bit-identical (÷1.0 is the identity).
 */
export function moveModsFromWeight(totalKg: number, carryMult = 1): MoveMods {
  // A negative or NaN weight is treated as an empty inventory (`NaN > 0` is
  // false) rather than reaching `stepMovement`. `Infinity` falls through to the
  // FLOOR: nonsense must never hand out a bonus.
  const raw = totalKg > 0 ? totalKg : 0;

  // A garbage multiplier is no multiplier — never a bonus, never a division by
  // zero.
  const mult = Number.isFinite(carryMult) && carryMult > 0 ? carryMult : 1;
  const kg = mult === 1 ? raw : raw / mult;

  if (kg <= CARRY_LIGHT_KG) return { speedMult: 1, accelMult: 1 };
  if (kg >= CARRY_MAX_KG) {
    return { speedMult: CARRY_SPEED_MIN_MULT, accelMult: CARRY_ACCEL_MIN_MULT };
  }

  // Both rails are returned as the authored constants, so the endpoints are
  // exact.
  const t = (kg - CARRY_LIGHT_KG) / (CARRY_MAX_KG - CARRY_LIGHT_KG);
  return {
    speedMult: 1 + (CARRY_SPEED_MIN_MULT - 1) * t,
    accelMult: 1 + (CARRY_ACCEL_MIN_MULT - 1) * t,
  };
}

/**
 * Fieldwise product of two sets of modifiers. Penalties STACK MULTIPLICATIVELY
 * — the only composition that cannot drive a multiplier negative. Both sides
 * combine health first, weight second. `staminaRegenMult` is written ONLY when
 * at least one side carried it, so legacy two-field `MoveMods` stay
 * byte-identical.
 */
export function combineMoveMods(a: MoveMods, b: MoveMods): MoveMods {
  const out: MoveMods = {
    speedMult: a.speedMult * b.speedMult,
    accelMult: a.accelMult * b.accelMult,
  };
  if (a.staminaRegenMult !== undefined || b.staminaRegenMult !== undefined) {
    out.staminaRegenMult = (a.staminaRegenMult ?? 1) * (b.staminaRegenMult ?? 1);
  }
  return out;
}
